
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface GeneralErrorProps {
  message: string;
  hasCorsError?: boolean;
  onRetry?: () => void;
}

export function GeneralError({ message, hasCorsError, onRetry }: GeneralErrorProps) {
  // Check if it's a collection URL format issue
  const isCollectionFormatError = message.toLowerCase().includes('url') && 
                                 !message.toLowerCase().includes('cors');
  
  // Check for network-related errors
  const isNetworkError = message.toLowerCase().includes('failed to fetch') || 
                         message.toLowerCase().includes('timeout') ||
                         message.toLowerCase().includes('network');
  
  const isProxyError = message.toLowerCase().includes('proxy');
  
  return (
    <Alert variant="destructive" className="mt-6 mx-auto max-w-3xl bg-red-900/30 border-red-800">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Error</AlertTitle>
      <AlertDescription>
        {message}
        
        {isCollectionFormatError && (
          <div className="mt-2 text-sm bg-black/20 p-2 rounded border border-red-800/50">
            <p className="font-medium mb-1">TikTok Collection URL Format:</p>
            <code className="block bg-black/30 p-1 rounded text-xs font-mono mb-2">
              https://www.tiktok.com/@username/collection/collection-id
            </code>
            <p className="text-xs">
              Example: https://www.tiktok.com/@username/collection/Tr2-1234567890123456789
            </p>
          </div>
        )}
        
        {isNetworkError && !hasCorsError && (
          <div className="mt-2 text-sm">
            <p>The application is having trouble connecting to the TikTok API. This might be due to:</p>
            <ul className="list-disc pl-5 mt-1 text-xs space-y-1">
              <li>Network connectivity issues</li>
              <li>The API might be temporarily unavailable</li>
              <li>Your internet connection might be unstable</li>
            </ul>
          </div>
        )}
        
        {isProxyError && (
          <div className="mt-2 text-sm bg-black/20 p-2 rounded border border-red-800/50">
            <p>The CORS proxy service may be experiencing issues. Try again later.</p>
          </div>
        )}
        
        {hasCorsError && (
          <div className="mt-2 text-sm">
            <p>The application is having trouble connecting to the TikTok API due to cross-origin restrictions.</p>
          </div>
        )}
        
        {onRetry && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onRetry}
            className="mt-3 bg-red-900/30 hover:bg-red-800/50 border-red-700"
          >
            <RefreshCw className="mr-2 h-3 w-3" />
            Try Again
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
